/**
 * WR-249. Is a BLOCKING SURFACE currently on screen?
 *
 * A "blocking surface" is a dialog that covers the drawing and takes the
 * keyboard while it is up — the twelve enumerated at wr249-s1. It is NOT a
 * side-strip panel: those are 280 px wide, do not cover the drawing, and are
 * governed separately by `data-panel-keyboard-policy` (see contextResolver).
 *
 * ⛔ THE DEFECT THIS CLOSES, measured: with any of six real dialogs mounted,
 * all 27 canvas bindings that fire with no dialog open still fired — the same
 * 27, exactly — and two of them removed the user's objects from the store.
 * Open any dialog, press Backspace, your selection is gone.
 *
 * ── WHY A DOM ATTRIBUTE AND NOT A REGISTRY ──────────────────────────────────
 *
 * This predicate is STATELESS ON PURPOSE, and that is the whole design.
 *
 * A ref-counted token acquired on mount and released on unmount was designed,
 * reviewed, and WITHDRAWN at wr249-s1 cycle 2. A token can LEAK: a cleanup that
 * throws before its release leaves the canvas keyboard permanently dead with no
 * dialog on screen — strictly worse than the bug it fixes. A `querySelector`
 * has no state and therefore cannot leak. The attribute is absent when the
 * surface is closed because every blocking surface `return null`s when closed
 * or is unmounted by its host — verified at each of the twelve.
 *
 * The house precedent is `data-text-editing`, read synchronously in this very
 * code path by `isTextEditing()` (contextResolver.ts:28).
 *
 * ⛔ NEVER key this on `role="dialog"`. ModularPanel.tsx:261 carries that role
 * with no `aria-modal`, fanning out to ~30 non-blocking side panels; keying on
 * it would kill the canvas keyboard behind every one of them.
 *
 * ⛔ KEEP THIS A LEAF. It imports nothing but the DOM. KeyboardShortcutService
 * calls it on every keypress, and a heavy import here reintroduces the
 * TDZ/boot-cycle shape KEYBOARD-SYSTEM.md §7 documents.
 *
 * Fail-open direction — a future blocking surface that forgets to declare
 * itself — is UNCOVERED per Playbook A15, dated 2026-08-24, with two owners:
 * (1) the live walk, and (2) the mint documentation, so the knowledge lands
 * where new surfaces are born.
 */
export const BLOCKING_SURFACE_ATTR = "data-blocking-surface";

export function isBlockingSurfaceOpen(): boolean {
  // `document` can be undefined in a non-DOM runtime (SSR, a node-env test).
  // Absent DOM means no surface, which is the armed-canvas default.
  if (typeof document === "undefined") return false;
  return document.querySelector(`[${BLOCKING_SURFACE_ATTR}]`) !== null;
}
